import { NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  UserPlus, Building2, CalendarClock, LogOut, Menu, X,
  ChevronsLeft, ChevronsRight, PhoneCall, BarChart3, Settings as SettingsIcon, Bot,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import ErrorBoundary from './ErrorBoundary.jsx'

const navItems = [
  { to: '/leads',             label: 'Lead',             icon: UserPlus },
  { to: '/companies',         label: 'Company',          icon: Building2 },
  { to: '/follow-ups',        label: 'Follow up',        icon: PhoneCall },
  { to: '/operations',        label: 'Operation',        icon: CalendarClock },
  { to: '/cst',               label: 'CST scores',       icon: BarChart3 },
  { to: '/bot',               label: 'Chatbot',          icon: Bot },
  { to: '/settings',          label: 'Settings',         icon: SettingsIcon },
]

const COLLAPSE_KEY = 'tt-sidebar-collapsed'

export default function Layout() {
  const [me, setMe]           = useState(null)
  const [navOpen, setNavOpen] = useState(false)
  const [botUnread, setBotUnread] = useState(0)
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(COLLAPSE_KEY) === '1'
  })
  const location = useLocation()

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0')
  }, [collapsed])

  useEffect(() => {
    fetch('/__tt/me')
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => j && setMe(j))
      .catch(() => {})
  }, [])

  useEffect(() => { setNavOpen(false) }, [location.pathname])

  // Unread staff notifications, on the Chatbot item.
  //
  // These were LINE pushes to a staff group until 2026-09-21. LINE bills a group
  // push once per member, so one new-lead alert cost five customer-messages in a
  // five-person group — and that is what exhausted the monthly quota and stopped
  // the inbox answering real people. The badge has to live HERE, not only inside
  // the inbox: a notification you have to already be looking at the inbox to see
  // is not a notification. Polled, because Pages Functions have no push channel.
  useEffect(() => {
    let alive = true
    const tick = () => fetch('/api/bot/alerts?unread=1')
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive && j) setBotUnread(j.unread || 0) })
      .catch(() => {})
    tick()
    const t = setInterval(tick, 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [location.pathname])

  useEffect(() => {
    if (!navOpen) return
    const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [navOpen])

  const email   = me?.email || ''
  const name    = me?.name  || ''
  const image   = me?.image || ''
  const initial = (name || email || '?').trim().charAt(0).toUpperCase()
  const showCompact = collapsed && !navOpen

  return (
    <div className="flex h-full">
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-20 h-12 bg-white/95 backdrop-blur border-b border-zinc-200/80 flex items-center justify-between px-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
            className="w-9 h-9 rounded-lg flex items-center justify-center text-zinc-700 hover:bg-zinc-100"
          >
            <Menu size={18} />
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Tooth Taxi" className="w-7 h-7 rounded-md object-cover shadow-sm" />
            <span className="text-[13px] font-semibold tracking-tight text-zinc-900">Tooth Taxi</span>
          </div>
        </div>
        <Avatar image={image} initial={initial} size={28} />
      </header>

      {navOpen && (
        <div
          onClick={() => setNavOpen(false)}
          className="md:hidden fixed inset-0 z-30 bg-zinc-900/30 backdrop-blur-[1px]"
        />
      )}

      <aside
        className={
          'fixed md:static inset-y-0 left-0 z-40 md:z-auto shrink-0 bg-white border-r border-zinc-200/80 flex flex-col ' +
          'transition-[transform,width] duration-200 ease-out ' +
          'w-60 ' +
          (showCompact ? 'md:w-[68px]' : 'md:w-60') + ' ' +
          (navOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0')
        }
      >
        {/* Brand */}
        <div className="px-3 md:px-4 pt-5 pb-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/logo.png" alt="Tooth Taxi" className="w-8 h-8 rounded-lg object-cover shadow-sm shrink-0" />
            <div className={'flex flex-col leading-tight min-w-0 ' + (showCompact ? 'md:hidden' : '')}>
              <span className="text-[13px] font-semibold tracking-tight text-zinc-900 truncate">Tooth Taxi</span>
              <span className="text-[10.5px] tracking-[0.16em] font-medium text-zinc-400 uppercase">CRM</span>
            </div>
          </div>
          <button
            onClick={() => setNavOpen(false)}
            className="md:hidden w-8 h-8 rounded-lg text-zinc-500 hover:bg-zinc-100 flex items-center justify-center shrink-0"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        </div>

        {/* Nav */}
        <nav className={'flex-1 pt-2 ' + (showCompact ? 'md:px-2' : 'px-2')}>
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              title={showCompact ? label : undefined}
              className={({ isActive }) =>
                'group flex items-center gap-3 mb-0.5 rounded-lg text-[13.5px] font-medium transition-colors ' +
                (showCompact ? 'md:justify-center md:px-2 md:py-2 px-3 py-2' : 'px-3 py-2') + ' ' +
                (isActive
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50')
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative flex items-center">
                    <Icon size={16} strokeWidth={2}
                      className={isActive ? 'text-amber-600' : 'text-zinc-400 group-hover:text-zinc-600'} />
                    {/* Collapsed, the label is gone and the count with it — so the
                        dot rides the icon instead, or the sidebar goes silent
                        exactly for the people who keep it narrow. */}
                    {to === '/bot' && botUnread > 0 && showCompact && (
                      <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-rose-600" />
                    )}
                  </span>
                  <span className={showCompact ? 'md:hidden' : ''}>{label}</span>
                  {to === '/bot' && botUnread > 0 && (
                    <span className={`ml-auto rounded-full bg-rose-600 px-1.5 text-[10px] font-semibold text-white ${
                      showCompact ? 'md:hidden' : ''}`}>
                      {botUnread}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className={
          'mt-2 mx-3 rounded-xl border border-zinc-200/80 bg-zinc-50/40 flex items-center gap-2.5 ' +
          (showCompact ? 'md:p-1.5 md:justify-center p-2.5' : 'p-2.5')
        }>
          <Avatar image={image} initial={initial} size={32} title={showCompact ? `${name || email || '—'} · sign out` : ''} />
          <div className={'flex-1 min-w-0 ' + (showCompact ? 'md:hidden' : '')}>
            <div className="text-[12px] font-medium text-zinc-900 truncate" title={name || email || ''}>
              {name || email || '—'}
            </div>
            <a href="/__tt/logout" className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900">
              <LogOut size={11} /> Sign out
            </a>
          </div>
        </div>

        {/* Collapse toggle — bottom of sidebar (doctor-portal style) */}
        <div className="hidden md:block px-3 pt-2 pb-3">
          <button
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            className={
              'w-full inline-flex items-center justify-center h-8 rounded-lg text-zinc-500 ' +
              'hover:bg-zinc-50 hover:text-zinc-900 transition-colors ' +
              (collapsed ? '' : 'gap-1.5 text-[11px] font-medium')
            }
          >
            {collapsed
              ? <ChevronsRight size={16} />
              : <><ChevronsLeft size={14} /><span>Collapse</span></>}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto pt-12 md:pt-0">
        <ErrorBoundary>
          <div key={location.pathname}>
            <Outlet />
          </div>
        </ErrorBoundary>
      </main>
    </div>
  )
}

/* Avatar — renders the Xano profile image when present, otherwise an amber
   gradient circle with the first initial. Matches the brand. */
function Avatar({ image, initial, size = 32, title }) {
  const dim = { width: size, height: size }
  const fontSize = Math.round(size * 0.4)
  if (image) {
    return (
      <img
        src={image} alt="" title={title}
        style={dim}
        onError={(e) => { e.currentTarget.style.display = 'none' /* graceful fallback if URL 404s */ }}
        className="rounded-full object-cover ring-1 ring-amber-200/60 shadow-sm shadow-amber-500/20 shrink-0"
      />
    )
  }
  return (
    <div
      title={title}
      style={dim}
      className="rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-semibold shadow-sm shadow-amber-500/30 ring-1 ring-amber-300/40 shrink-0"
    >
      <span style={{ fontSize }}>{initial}</span>
    </div>
  )
}
